import { useAuthorization } from '@autone/auth';
import { useAutoneTranslation } from '@autone/translations';
import {
  ASSORTMENT_ADMIN,
  ASSORTMENT_READONLY,
  ASSORTMENT_STANDARD,
  EVENTS_ADMIN,
  EVENTS_READONLY,
  EVENTS_STANDARD,
  PARAMETERS_ADMIN,
  PARAMETERS_READONLY,
} from '@autone/utils';
import { useMemo } from 'react';

import {
  CalendarIcon,
  NewAssortmentIcon,
  SettingsIcon,
} from '../../../../icons';
import { useNavigationContext } from '../../context';
import {
  ASSORTMENT_PATH_APP,
  EVENTS_PATH_APP,
  HUB_PATH_APP,
} from '../../routes';
import { createSidebarItem } from '../../utilities/createSidebarItem';
import type { CreateSidebarItemParams } from '../use-app-sidebar/useAppSidebar.types';
import { useWithClientRouting } from '../useWithClientRouting';

const ACCOUNT_CONFIG: Record<string, CreateSidebarItemParams> = {
  assortment: {
    titleKey: 'sidenav.sections.account.items.assortment.name',
    icon: NewAssortmentIcon,
    path: ASSORTMENT_PATH_APP.inSeason,
    permissions: [ASSORTMENT_READONLY, ASSORTMENT_STANDARD, ASSORTMENT_ADMIN],
  },
  events: {
    titleKey: 'sidenav.sections.account.items.events.name',
    icon: CalendarIcon,
    path: EVENTS_PATH_APP.home,
    permissions: [EVENTS_READONLY, EVENTS_STANDARD, EVENTS_ADMIN],
  },
  parameters: {
    titleKey: 'sidenav.sections.account.items.parameters.name',
    icon: SettingsIcon,
    path: HUB_PATH_APP.parameters,
    permissions: [PARAMETERS_READONLY, PARAMETERS_ADMIN],
  },
} as const;

export const useSidebarAccount = () => {
  const { common } = useAutoneTranslation();
  const { currentPathname } = useNavigationContext();
  const { isAuthorized } = useAuthorization();
  const withClientRouting = useWithClientRouting();

  return useMemo(
    () => ({
      account: Object.values(ACCOUNT_CONFIG).map((accountConfig) =>
        createSidebarItem({
          config: accountConfig,
          common,
          isAuthorized,
          currentPathname,
          withClientRouting,
        }),
      ),
    }),
    [common, isAuthorized, currentPathname, withClientRouting],
  );
};
